'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

/** Below this width the video is never fetched — see note in the effect. */
const VIDEO_MIN_WIDTH = 1024

/**
 * Hero media with a fast-LCP strategy:
 * - The poster image is the LCP element: rendered in initial HTML via
 *   next/image with priority + fetchPriority="high".
 * - The <video> is NOT in the initial HTML. It mounts only after the window
 *   `load` event and an idle callback, so its ~3MB download can never compete
 *   with the LCP image or first paint for bandwidth.
 * - It is skipped entirely on phones, on metered/slow connections, and for
 *   users who prefer reduced motion.
 * - Poster is the first frame of the video (same framing), and the video fades
 *   in only after it can play through, so there is no visible pop on swap.
 */
export function HeroMedia() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const [videoReady, setVideoReady] = useState(false)

  useEffect(() => {
    // Never load the video for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    // The hero video is ~3MB — on a phone that is an order of magnitude more
    // than the rest of the page combined, and it buys very little: the mobile
    // hero is a tall 1:1 crop where the poster (the video's own first frame)
    // reads essentially the same. Desktop still gets the motion.
    if (window.innerWidth < VIDEO_MIN_WIDTH) return

    // Skip on metered or slow connections and fall back to the poster, which
    // is already the LCP image.
    const connection = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string }
      }
    ).connection
    if (connection?.saveData) return
    if (connection?.effectiveType && connection.effectiveType !== '4g') return

    let observer: IntersectionObserver | undefined
    let idleHandle: number | undefined

    // Only start watching once the page has fully loaded, so the video never
    // contends with render-critical requests.
    const start = () => {
      const schedule =
        window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200))
      idleHandle = schedule(() => {
        const el = containerRef.current
        if (!el) return
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              setShouldLoadVideo(true)
              observer?.disconnect()
            }
          },
          { rootMargin: '200px' }
        )
        observer.observe(el)
      }) as unknown as number
    }

    if (document.readyState === 'complete') {
      start()
    } else {
      window.addEventListener('load', start, { once: true })
    }

    return () => {
      window.removeEventListener('load', start)
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle)
      observer?.disconnect()
    }
  }, [])

  return (
    <div ref={containerRef} className="absolute inset-0">
      {/* Poster image: the LCP element, renders immediately */}
      <Image
        src="/images/hero-banner-poster.avif"
        alt="Aircraft on the ramp at Michigan Aviation, Oakland County International Airport"
        width={1920}
        height={1080}
        priority
        fetchPriority="high"
        sizes="100vw"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Video mounts after first paint; fades in over the poster once playable */}
      {shouldLoadVideo && (
        <video
          muted
          playsInline
          loop
          autoPlay
          preload="none"
          onCanPlayThrough={() => setVideoReady(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            videoReady ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <source src="/videos/hero-banner.webm" type="video/webm" />
          <source src="/videos/hero-banner.mp4" type="video/mp4" />
        </video>
      )}
    </div>
  )
}
